import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Custom hook to handle unsaved changes warnings
 * Prevents navigation when there are unsaved changes and shows browser prompt
 */
export const useUnsavedChanges = (hasUnsavedChanges, isActive = true) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Handle browser refresh/close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && isActive) {
        e.preventDefault();
        e.returnValue = 'Your changes have not been saved - are you sure you want to leave this page?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, isActive]);

  // Handle React Router navigation
  useEffect(() => {
    const handlePopState = () => {
      if (hasUnsavedChanges && isActive) {
        const confirmLeave = window.confirm('Your changes have not been saved - are you sure you want to leave this page?');
        if (!confirmLeave) {
          // Push the current location back to prevent navigation
          window.history.pushState(null, '', location.pathname + location.search);
        }
      }
    };

    // Listen for back/forward button
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasUnsavedChanges, isActive, location]);

  // Function to navigate with confirmation if needed
  const navigateWithConfirmation = useCallback((path, options = {}) => {
    if (hasUnsavedChanges && isActive) {
      const confirmLeave = window.confirm('Your changes have not been saved - are you sure you want to leave this page?');
      if (confirmLeave) {
        navigate(path, options);
      }
      // If they click "Stay", do nothing - they remain on the page
    } else {
      navigate(path, options);
    }
  }, [hasUnsavedChanges, isActive, navigate]);

  return {
    navigateWithConfirmation,
    hasUnsavedChanges: hasUnsavedChanges && isActive
  };
};

export default useUnsavedChanges;